// src/components/security/TotpCard.tsx
/**
 * Enrolment, re-enrolment and recovery-code regeneration — the card that turns the second factor on.
 *
 * THE ONE THING TO KNOW BEFORE CHANGING THIS FILE. `POST /v1/mfa/enroll` is destructive. There is one
 * secret column on the user row, not a pending one and a live one, so calling it replaces the stored
 * secret immediately and stops the authenticator entry the admin already has from producing valid
 * codes. `verifyEnrollment` is what makes the new one usable. Between those two calls the account is
 * reachable only through a recovery code or the other admin's peer reset.
 *
 * Everything unusual about this component follows from that:
 *
 * - `enrollTotp()` is called from a click handler and NEVER from an effect. An effect that fires on
 *   a re-render — a status refresh, a Fast Refresh, a StrictMode double-mount — would silently burn
 *   the owner's working second factor.
 * - Turning it on again asks first (`confirmReenrol`), in a `Danger` button, with the consequence
 *   spelled out rather than implied.
 * - Turning it on again is offered only to a session that has already passed its second factor. The
 *   server refuses the other case outright (`403 mfa_required` — replacing an enrolment needs the
 *   factor it replaces), so showing the button to an unverified session would be an invitation to a
 *   refusal; the "Overiť teraz" hint below offers the step that actually helps instead.
 * - "Zrušiť" on the scan step does not undo anything, and says so when there was something to undo.
 * - The page above this card refuses to render it at all when the status could not be read, rather
 *   than defaulting to "not enabled" and offering the destructive button on a guess.
 *
 * The phase machine is explicit for the same reason: every transition into `enrolling` is a
 * deliberate click, and every busy phase disables the control that produced it, so an impatient
 * second click cannot start a second enrolment.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import { errText, isRateLimited } from '../../api/errors';
import { enrollTotp, regenerateRecoveryCodes, verifyEnrollment } from '../../api/securityApi';
import {
    TOTP_CODE_LENGTH,
    TotpEnrollment,
    VERIFY_RATE_LIMIT_HINT,
    isTotpCodeComplete,
    normalizeTotpCode,
} from '../../security/mfaContract';
import { openStepUp, recoveryCodesReplaced } from '../../store/mfaSlice';
import { loadMfaStatus } from '../../store/mfaThunks';
import { AppDispatch } from '../../store/store';
import { copyText } from './browser';
import RecoveryCodesPanel from './RecoveryCodesPanel';
import {
    Banner,
    Card,
    CardTitle,
    CodeInput,
    Danger,
    Field,
    Hint,
    Label,
    LinkButton,
    Mono,
    Primary,
    Row,
    Secondary,
    WarnBox,
} from './ui';

/* ---------------- copy ---------------- */

const ENROLL_FAILED = 'Nepodarilo sa začať nastavenie dvojstupňového overenia.';
const VERIFY_FAILED = 'Overenie zlyhalo. Skúste to znova.';
const REGENERATE_FAILED = 'Nepodarilo sa vygenerovať nové záložné kódy.';
const INCOMPLETE_CODE = 'Zadajte celý šesťciferný kód z aplikácie.';

const ENABLED_MESSAGE = 'Dvojstupňové overenie je zapnuté a táto relácia je overená.';
const REGENERATED_MESSAGE = 'Záložné kódy boli obnovené.';

/** The server accepted the code but the body carried no codes — an older jar, or a truncated
 *  response. Enrolment itself succeeded, so the screen must say so and must not show an empty grid
 *  or claim a count it was never told. */
const NO_CODES_SHOWN =
    'Dvojstupňové overenie je zapnuté, ale záložné kódy sa nepodarilo zobraziť. Vygenerujte si nové.';

const REENROL_CANCELLED =
    'Nastavenie ste prerušili. Doterajší záznam v aplikácii už neplatí — spustite nastavenie znova, ' +
    'inak sa prihlásite už len záložným kódom alebo cez druhého správcu.';

const ENROL_CANCELLED = 'Nastavenie ste prerušili. Dvojstupňové overenie zostáva vypnuté.';

const REENROL_WARNING =
    'Nové nastavenie okamžite zneplatní doterajší záznam v autentifikačnej aplikácii. Ak nastavenie ' +
    'nedokončíte, prihlásite sa už len záložným kódom alebo cez druhého správcu.';

const SCAN_WARNING =
    'Doterajší záznam v aplikácii už neplatí. Dokončite nastavenie teraz — inak sa prihlásite už len ' +
    'záložným kódom alebo cez druhého správcu.';

/**
 * Slovak counts the noun with the number, so the three cases below are three sentences rather than
 * one with a placeholder. The threshold is two, which is why "kódy" (the 2–4 form) covers the whole
 * plural branch.
 */
function lowCodesWarning(remaining: number): string {
    if (remaining <= 0) {
        return (
            'Nemáte už žiadne záložné kódy. Ak stratíte prístup k autentifikačnej aplikácii, dostane ' +
            'vás dnu už len druhý správca.'
        );
    }
    if (remaining === 1) {
        return 'Zostáva vám už len 1 záložný kód. Vygenerujte si nové, kým máte prístup k aplikácii.';
    }
    return (
        'Zostávajú vám už len ' +
        remaining +
        ' záložné kódy. Vygenerujte si nové, kým máte prístup k aplikácii.'
    );
}

/** `JBSWY3DPEHPK3PXP` → `JBSW Y3DP EHPK 3PXP`. The key is typed by hand into a phone when the camera
 *  is not an option; four-character groups are what every authenticator's own manual-entry screen
 *  shows, and they make a dropped character visible. */
function groupInFours(secret: string): string {
    const clean = (typeof secret === 'string' ? secret : '').replace(/\s+/g, '');
    const parts: string[] = [];
    for (let i = 0; i < clean.length; i += 4) {
        parts.push(clean.slice(i, i + 4));
    }
    return parts.join(' ');
}

/* ---------------- component ---------------- */

type Phase =
    | 'idle'
    | 'confirmReenrol'
    | 'enrolling'
    | 'scan'
    | 'verifying'
    | 'codes'
    | 'confirmRegenerate'
    | 'regenerating'
    | 'regenerated';

type Notice = { text: string; tone: 'ok' | 'warn' };

interface TotpCardProps {
    enabled: boolean;
    sessionVerified: boolean;
    recoveryCodesRemaining: number | null;
}

const TotpCard: React.FC<TotpCardProps> = ({ enabled, sessionVerified, recoveryCodesRemaining }) => {
    const dispatch = useDispatch<AppDispatch>();

    const [phase, setPhase] = useState<Phase>('idle');
    const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
    const [codes, setCodes] = useState<string[]>([]);
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<Notice | null>(null);
    const [keyNotice, setKeyNotice] = useState<string | null>(null);
    /** Whether the enrolment now in flight is replacing a working second factor. Drives the standing
     *  warning on the scan step and the wording when the admin backs out of it. */
    const [reenrolling, setReenrolling] = useState(false);

    const codeInput = useRef<HTMLInputElement>(null);

    const busy = phase === 'enrolling' || phase === 'verifying' || phase === 'regenerating';

    // Entering the scan step focuses the field, and so does coming back to it from a rejected code —
    // a wrong digit should cost one keystroke to fix, not a click and then the keystrokes.
    useEffect(() => {
        if (phase === 'scan' && codeInput.current) {
            codeInput.current.focus();
        }
    }, [phase]);

    /* ---------------- actions ---------------- */

    const startEnrolment = async (isReenrol: boolean) => {
        if (busy) {
            return;
        }
        setReenrolling(isReenrol);
        setError(null);
        setNotice(null);
        setKeyNotice(null);
        setPhase('enrolling');
        try {
            const started = await enrollTotp();
            setEnrollment(started);
            setCode('');
            setPhase('scan');
        } catch (err) {
            // Never log the error object itself: it carries the request headers, and those carry the
            // bearer token.
            setError(errText(err, ENROLL_FAILED));
            setPhase(isReenrol ? 'confirmReenrol' : 'idle');
        }
    };

    const submitCode = async (event: React.FormEvent) => {
        event.preventDefault();
        if (phase !== 'scan') {
            return;
        }
        const normalized = normalizeTotpCode(code);
        if (!isTotpCodeComplete(normalized)) {
            setError(INCOMPLETE_CODE);
            return;
        }
        setError(null);
        setPhase('verifying');
        try {
            const issued = await verifyEnrollment(normalized);
            setEnrollment(null);
            setCode('');
            if (issued.length === 0) {
                setPhase('idle');
                setNotice({ text: NO_CODES_SHOWN, tone: 'warn' });
                dispatch(loadMfaStatus({ force: true }));
                return;
            }
            setCodes(issued);
            setPhase('codes');
        } catch (err) {
            const text = errText(err, VERIFY_FAILED);
            setError(isRateLimited(err) ? text + ' ' + VERIFY_RATE_LIMIT_HINT : text);
            setCode('');
            setPhase('scan'); // the effect above puts the caret back in the empty field
        }
    };

    const regenerate = async () => {
        if (busy) {
            return;
        }
        setError(null);
        setNotice(null);
        setPhase('regenerating');
        try {
            const issued = await regenerateRecoveryCodes();
            if (issued.length === 0) {
                setPhase('idle');
                setNotice({ text: NO_CODES_SHOWN, tone: 'warn' });
                dispatch(loadMfaStatus({ force: true }));
                return;
            }
            setCodes(issued);
            setPhase('regenerated');
        } catch (err) {
            const text = errText(err, REGENERATE_FAILED);
            setError(isRateLimited(err) ? text + ' ' + VERIFY_RATE_LIMIT_HINT : text);
            setPhase('confirmRegenerate');
        }
    };

    /**
     * The codes have been taken away. Two dispatches on purpose: `recoveryCodesReplaced` states what
     * this component knows for certain and unlocks the cards below it in the same render, and the
     * forced status reload replaces every one of those values with the server's own answer — which is
     * also the only thing that can correct them if the two ever disagree.
     */
    const acknowledge = (message: string) => {
        const issuedCount = codes.length;
        setCodes([]);
        setError(null);
        setNotice({ text: message, tone: 'ok' });
        setPhase('idle');
        dispatch(recoveryCodesReplaced(issuedCount));
        dispatch(loadMfaStatus({ force: true }));
    };

    const cancelScan = () => {
        setPhase('idle');
        setEnrollment(null);
        setCode('');
        setError(null);
        setKeyNotice(null);
        // The secret on the server has already been replaced — the cancel button undoes nothing, and
        // pretending otherwise is how an admin ends up locked out believing they are fine.
        setNotice({
            text: reenrolling ? REENROL_CANCELLED : ENROL_CANCELLED,
            tone: reenrolling ? 'warn' : 'ok',
        });
    };

    const copyKey = async () => {
        if (!enrollment) {
            return;
        }
        // The RAW secret, not the four-character grouping shown above it: the spaces are a reading
        // aid, and some authenticators reject a key that carries them.
        const ok = await copyText(enrollment.base32Secret);
        setKeyNotice(
            ok
                ? 'Kľúč je v schránke.'
                : 'Kopírovanie sa nepodarilo. Označte kľúč myšou a skopírujte ho ručne.'
        );
    };

    /* ---------------- screens ---------------- */

    const renderIdle = () => {
        if (!enabled) {
            return (
                <>
                    <Banner $warn>Dvojstupňové overenie nie je zapnuté.</Banner>
                    <Hint style={{ margin: '0 0 14px' }}>
                        Chráni faktúry, kampane a údaje členov: po prihlásení si raz za reláciu
                        vypýtame šesťciferný kód z autentifikačnej aplikácie (Google Authenticator,
                        1Password, Authy a podobne).
                    </Hint>
                    <Primary type="button" disabled={busy} onClick={() => startEnrolment(false)}>
                        {phase === 'enrolling' ? 'Pripravujem…' : 'Zapnúť dvojstupňové overenie'}
                    </Primary>
                </>
            );
        }

        const lowCodes = recoveryCodesRemaining !== null && recoveryCodesRemaining <= 2;
        return (
            <>
                <Banner $ok>Dvojstupňové overenie je zapnuté.</Banner>
                {lowCodes && <WarnBox>{lowCodesWarning(recoveryCodesRemaining as number)}</WarnBox>}
                {sessionVerified && (
                    <Row>
                        <Secondary
                            type="button"
                            disabled={busy}
                            onClick={() => setPhase('confirmReenrol')}
                        >
                            Nastaviť znova (nová aplikácia alebo telefón)
                        </Secondary>
                        <Secondary
                            type="button"
                            disabled={busy}
                            onClick={() => {
                                setError(null);
                                setNotice(null);
                                setPhase('confirmRegenerate');
                            }}
                        >
                            Vygenerovať nové záložné kódy
                        </Secondary>
                    </Row>
                )}
                {!sessionVerified && (
                    <Hint>
                        Nastaviť overenie znova alebo vygenerovať nové záložné kódy sa dá až po
                        overení tejto relácie.{' '}
                        <LinkButton
                            type="button"
                            onClick={() => dispatch(openStepUp({ blocking: false }))}
                        >
                            Overiť teraz
                        </LinkButton>
                    </Hint>
                )}
                {recoveryCodesRemaining !== null && !lowCodes && (
                    <Hint>Nepoužité záložné kódy: {recoveryCodesRemaining}.</Hint>
                )}
            </>
        );
    };

    const renderConfirmReenrol = () => (
        <>
            <WarnBox>{REENROL_WARNING}</WarnBox>
            <Row>
                <Danger type="button" disabled={busy} onClick={() => startEnrolment(true)}>
                    {phase === 'enrolling' ? 'Pripravujem…' : 'Áno, začať odznova'}
                </Danger>
                <Secondary type="button" disabled={busy} onClick={() => setPhase('idle')}>
                    Zrušiť
                </Secondary>
            </Row>
        </>
    );

    const renderScan = () => {
        if (!enrollment) {
            return null;
        }
        return (
            <>
                {reenrolling && <WarnBox>{SCAN_WARNING}</WarnBox>}

                <Step>1. Naskenujte QR kód</Step>
                <QrBox>
                    {/* Rendered in this browser, never fetched from a QR service: the otpauth URI IS
                        the secret, and handing it to a third party would hand over the second factor. */}
                    <QRCodeSVG value={enrollment.otpAuthUri} size={200} level="M" marginSize={2} />
                </QrBox>

                <Label as="div">Kľúč pre ručné zadanie</Label>
                <Mono>{groupInFours(enrollment.base32Secret)}</Mono>
                <Row style={{ marginTop: 10 }}>
                    <Secondary type="button" onClick={copyKey}>
                        Skopírovať kľúč
                    </Secondary>
                </Row>
                {keyNotice && <Hint>{keyNotice}</Hint>}

                <form onSubmit={submitCode}>
                    <Field style={{ marginTop: 18 }}>
                        <Label htmlFor="totp-enroll-code">2. Zadajte kód z aplikácie</Label>
                        <CodeInput
                            id="totp-enroll-code"
                            ref={codeInput}
                            name="totp"
                            value={code}
                            onChange={(e) => setCode(normalizeTotpCode(e.target.value))}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete="one-time-code"
                            maxLength={TOTP_CODE_LENGTH}
                            placeholder="000000"
                            disabled={busy}
                        />
                    </Field>
                    <Row>
                        <Primary type="submit" disabled={busy}>
                            {phase === 'verifying' ? 'Overujem…' : 'Overiť a zapnúť'}
                        </Primary>
                        <Secondary type="button" disabled={busy} onClick={cancelScan}>
                            Zrušiť
                        </Secondary>
                    </Row>
                </form>
            </>
        );
    };

    const renderConfirmRegenerate = () => (
        <>
            <WarnBox>Doterajšie záložné kódy prestanú platiť.</WarnBox>
            <Row>
                <Danger type="button" disabled={busy} onClick={regenerate}>
                    {phase === 'regenerating' ? 'Generujem…' : 'Áno, vygenerovať nové'}
                </Danger>
                <Secondary type="button" disabled={busy} onClick={() => setPhase('idle')}>
                    Zrušiť
                </Secondary>
            </Row>
        </>
    );

    const renderCodes = (title: string, message: string) => (
        <RecoveryCodesPanel codes={codes} title={title} onAcknowledge={() => acknowledge(message)} />
    );

    let body: React.ReactNode;
    if (phase === 'confirmReenrol' || (phase === 'enrolling' && reenrolling)) {
        body = renderConfirmReenrol();
    } else if (phase === 'scan' || phase === 'verifying') {
        body = renderScan();
    } else if (phase === 'codes') {
        body = renderCodes('Záložné kódy', ENABLED_MESSAGE);
    } else if (phase === 'regenerated') {
        body = renderCodes('Nové záložné kódy', REGENERATED_MESSAGE);
    } else if (phase === 'confirmRegenerate' || phase === 'regenerating') {
        body = renderConfirmRegenerate();
    } else {
        body = renderIdle();
    }

    return (
        <Card>
            <CardTitle>Dvojstupňové overenie</CardTitle>
            {error && <Banner>{error}</Banner>}
            {notice && (
                <Banner $ok={notice.tone === 'ok'} $warn={notice.tone === 'warn'}>
                    {notice.text}
                </Banner>
            )}
            {body}
        </Card>
    );
};

/* ---------------- styles ---------------- */

const Step = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #141210;
    margin-bottom: 10px;
`;

const QrBox = styled.div`
    display: inline-block;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 16px;
`;

export default TotpCard;
